import { useQuery } from "@apollo/client";
import Edit from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Link,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { GetWasteContainerQuery } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useTenant } from "hooks/useTenant";
import { ReactNode, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";

import { GET_WASTE_CONTAINER_QUERY } from "./api";
import {
  containerTypeLabel,
  NO_VALUE_PLACEHOLDER,
  formatContainerDate,
  formatQuantity,
} from "./containerDisplay";
import { WasteContainerDialog } from "./WasteContainerDialog";
import { WasteContainerStatusChip } from "./WasteContainerStatusChip";

type WasteContainerDetailData = NonNullable<GetWasteContainerQuery["wasteContainer"]>;

function yesNo(value: boolean): string {
  return value ? "Yes" : "No";
}

function FieldValue({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{children}</Typography>
    </Grid>
  );
}

function SectionCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

/** Muted empty state for sections whose engines land in later milestones. */
function ComingSoonSection({ title, message }: { title: string; message: string }) {
  return (
    <SectionCard title={title}>
      <Box
        sx={{
          py: 4,
          textAlign: "center",
          backgroundColor: "grey.50",
          borderRadius: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {message}
        </Typography>
      </Box>
    </SectionCard>
  );
}

function ContainerDetailContent({ container }: { container: WasteContainerDetailData }) {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { tenantId } = useTenant();
  const isArchived = !!container.archivedAt;

  return (
    <Stack spacing={3} sx={{ px: 3, py: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5">{container.containerLabel}</Typography>
          <WasteContainerStatusChip status={container.status} />
          {isArchived && <Chip label="Archived" size="small" variant="outlined" />}
        </Stack>
        {!isArchived && (
          <Button
            variant="contained"
            size="small"
            startIcon={<Edit />}
            onClick={() => setEditDialogOpen(true)}
          >
            Edit
          </Button>
        )}
      </Stack>

      {isArchived && (
        <Alert severity="info" variant="outlined">
          This container was archived on {formatContainerDate(container.archivedAt)} and is
          read-only.
        </Alert>
      )}

      <SectionCard title="Details">
        <Grid container spacing={2}>
          <FieldValue label="Facility">
            {container.facility?.name ? (
              <Link
                component={RouterLink}
                to={`/o/${tenantId}/waste/facilities/${container.facilityId}`}
              >
                {container.facility.name}
              </Link>
            ) : (
              NO_VALUE_PLACEHOLDER
            )}
          </FieldValue>
          <FieldValue label="Container type">
            {containerTypeLabel(container.containerType)}
          </FieldValue>
          <FieldValue label="Capacity">
            {formatQuantity(container.capacity, container.capacityUnit)}
          </FieldValue>
          <FieldValue label="Amount on hand">
            {formatQuantity(container.amount, container.amountUnit)}
            {container.isEstimate && container.amount != null ? " (estimate)" : ""}
          </FieldValue>
          <FieldValue label="Waste stream">
            {container.wasteStream?.name ?? NO_VALUE_PLACEHOLDER}
          </FieldValue>
          <FieldValue label="Accumulation area">
            {container.accumulationArea?.name ?? NO_VALUE_PLACEHOLDER}
          </FieldValue>
          <FieldValue label="Accumulation start date">
            {formatContainerDate(container.accumulationStartDate)}
          </FieldValue>
          <FieldValue label="Moved to central date">
            {formatContainerDate(container.movedToCentralDate)}
          </FieldValue>
          <FieldValue label="Estimated pickup date">
            {formatContainerDate(container.estimatedPickupDate)}
          </FieldValue>
          <FieldValue label="Permanent vessel (tank)">{yesNo(container.isPermanent)}</FieldValue>
          <FieldValue label="Flow-through">{yesNo(container.isFlowThrough)}</FieldValue>
          <FieldValue label="Amounts are estimates">{yesNo(container.isEstimate)}</FieldValue>
          <FieldValue label="SQG shipping 200+ miles">
            {yesNo(container.sqgOver200Miles)}
          </FieldValue>
        </Grid>
      </SectionCard>

      <ComingSoonSection
        title="Accumulation clock"
        message="Accumulation clock limits and deadlines are coming soon."
      />
      <ComingSoonSection
        title="Fill history"
        message="Fill events and consolidation history are coming soon."
      />

      {editDialogOpen && (
        <WasteContainerDialog container={container} onClose={() => setEditDialogOpen(false)} />
      )}
    </Stack>
  );
}

export function WasteContainerDetail() {
  const { containerId } = useParams<{ containerId: string }>();
  const { tenantId } = useTenant();

  const { data, loading, error } = useQuery(GET_WASTE_CONTAINER_QUERY, {
    variables: { id: containerId ?? "" },
    skip: !containerId,
  });
  const container = data?.wasteContainer;

  useBreadcrumb([
    {
      label: "Waste",
      to: tenantId ? `/o/${tenantId}/waste` : undefined,
    },
    {
      label: "Containers",
      to: tenantId ? `/o/${tenantId}/waste/containers` : undefined,
    },
    {
      label: container?.containerLabel ?? "Container",
    },
  ]);

  if (loading && !container) {
    return (
      <Stack spacing={2} sx={{ px: 3, py: 2 }}>
        <Skeleton variant="rectangular" width="100%" height={48} />
        <Skeleton variant="rectangular" width="100%" height={280} />
        <Skeleton variant="rectangular" width="100%" height={120} />
      </Stack>
    );
  }

  if (error || !container) {
    return (
      <Stack spacing={1} sx={{ px: 3, py: 4 }} alignItems="center">
        <Typography variant="h6">Container not found</Typography>
        <Typography variant="body2" color="text.secondary">
          The container may have been archived, or the link may be incorrect.
        </Typography>
      </Stack>
    );
  }

  return <ContainerDetailContent container={container} />;
}
